/**
 * Confine a post-login redirect to this application (F87, audit #7).
 *
 * `returnUrl` is read straight from the query string and handed to `navigate()`.
 * React Router will navigate to an absolute URL quite happily, so
 * `?returnUrl=//evil.com` sends the user to an attacker's page the instant a
 * real login succeeds. That is the moment they are least likely to check the
 * address bar and most likely to retype a password.
 *
 * The rule is an allowlist of shape rather than a denylist of hosts: a return
 * URL must be a path on this origin. Anything else falls back to the default.
 *
 * Rejected, and why each one matters:
 *
 *   //evil.com           protocol-relative, the audit's own example
 *   https://evil.com     absolute
 *   javascript:alert(1)  a scheme that executes rather than navigates
 *   \/\/evil.com         backslashes, which some URL parsers fold to slashes
 *   /\u0009/evil.com      control characters, which browsers strip while parsing
 *   ''                   navigates nowhere useful
 */

const DEFAULT_RETURN_URL = '/projects'

export function safeReturnUrl(
  candidate: string | null | undefined,
  fallback: string = DEFAULT_RETURN_URL,
): string {
  if (!candidate) return fallback

  // Some URL parsers fold backslashes to forward slashes, so a value like
  // `\/\/evil.com` can slip past a check that only looks for a leading `//`.
  const value = candidate.replace(/\\/g, '/')

  // Control characters are stripped by browsers while resolving a URL, so they
  // can be used to hide any of the patterns below. Reject before inspecting.
  // Scanned rather than matched with a regex: a control-character class trips
  // eslint's no-control-regex, and the rule is right that such a class is
  // usually a mistake.
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i)
    if (code <= 0x1f || code === 0x7f) return fallback
  }

  // Must be a path on this origin.
  if (!value.startsWith('/')) return fallback

  // `//host` is protocol-relative: it leaves the origin while looking relative.
  if (value.startsWith('//')) return fallback

  // A scheme before any path separator means this is not a path at all.
  if (/^[a-z][a-z0-9+.-]*:/i.test(value)) return fallback

  return value
}

export default safeReturnUrl
